import { Component, OnInit, OnDestroy, signal, computed, inject, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { RestProviderService } from '../../../services/rest-provider/rest-provider.service';
import { RequestModel } from '../../../models/request/request-model';
import { NavigationRequestModel } from '../../../models/navigation/navigation-request-model';
import { NavigationResponseModel } from '../../../models/navigation/navigation-response-model';
import { NavigationNodeSerializer } from '../../../models/serializing/navigation-node-serializer';
import { DynamicLoadingUtil } from '../../../shared/helpers/dynamic-loading-util';
import { RouteNavigationService } from '../../../services/dynamic-routes-loader/route-navigation.service';
import { UtilityService } from '../../../services/utility/utility.service';
import { TranslationsProviderService } from '../../../services/translations-provider/translations-provider.service';
import { LocalStorageService } from '../../../services/local-storage-service/local-storage.service';
import { environment } from 'src/environments/environment';

/** URLs from the current route's breadcrumb chain, used to match detail pages to parent menu items */
function getBreadcrumbUrls(): string[] {
  const route = DynamicLoadingUtil.currentRoute;
  if (!route?.BreadCrumbs) return [];
  return route.BreadCrumbs.map((bc: any) => bc.Url).filter((u: string) => !!u && !u.includes('dashboard'));
}

// --- Display models for the template ---

interface DisplayNode {
  id: number;
  title: string;
  icon: string | null;
  url: string;
  children: DisplayNode[];
  isLeaf: boolean;
  depth: number;
}

@Component({
  selector: 'app-wg-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './wg-sidebar.component.html',
  styleUrls: ['./wg-sidebar.component.css']
})
export class WgSidebarComponent implements OnInit, OnDestroy {
  @Output() sidebarClicked = new EventEmitter<boolean>();

  private router = inject(Router);
  private restProvider = inject(RestProviderService);
  private dynamicRoutesLoader = inject(RouteNavigationService);
  private utility = inject(UtilityService);
  public translationsService = inject(TranslationsProviderService);
  private localStorageService = inject(LocalStorageService);

  private routerSub: Subscription;

  readonly result = signal<any[]>([]);
  readonly expandedNodes = signal<Set<number>>(new Set());
  readonly currentUrl = signal('');

  readonly projectName: string = environment.projectSidebarName || '';
  readonly projectSubtitle: string = (environment as any).projectSidebarSubtitle || '';
  readonly imgPath: string = environment.sidebarIcon || '';
  readonly sidebarTitleType: string = (environment as any).sidebarTitleType || 'titleSubtitle';

  readonly displayNodes = computed<DisplayNode[]>(() => {
    const nodes = this.result().map(node => this.toDisplayNode(node, 0)).filter(n => n !== null) as DisplayNode[];
    // Deduplicate root nodes by title (API may return the same section twice)
    const seen = new Set<string>();
    return nodes.filter(n => {
      if (seen.has(n.title)) return false;
      seen.add(n.title);
      return true;
    });
  });

  readonly isDashboardActive = computed(() => {
    const url = this.currentUrl();
    return url.includes('dashboard');
  });

  /** All top-level section IDs for accordion behavior */
  private sectionIds = computed(() => this.displayNodes().filter(n => !n.isLeaf).map(n => n.id));

  ngOnInit() {
    this.loadMenuItems();
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl.set(event.url);
        this.autoExpandActiveSection();
      });
    this.currentUrl.set(this.router.url);
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  // --- Data loading (same API call as SemLeftMenuComponent) ---

  private loadMenuItems() {
    const model = new RequestModel();
    model.url = '/api/navigation/getNodes';
    const request = new NavigationRequestModel();
    request.context = 'left-menu';
    request.depth = 0;
    request.rootNode = null;
    model.data = request;

    this.restProvider.serializer = new NavigationNodeSerializer();

    let cacheData: string | null = null;
    if (environment.useRouteAndMasterpageCache) {
      cacheData = this.localStorageService.get('/api/navigation/getNodes_left-menu');
    }

    if (cacheData) {
      const data = JSON.parse(cacheData);
      DynamicLoadingUtil.navigationNodes = data;
      this.dynamicRoutesLoader.leftMenuNodes = data;
      this.result.set(data);
    } else {
      this.restProvider.getDataPOST<NavigationResponseModel[]>(model).subscribe(
        data => {
          DynamicLoadingUtil.navigationNodes = data;
          this.dynamicRoutesLoader.leftMenuNodes = data;
          this.result.set(data);
          this.localStorageService.add('/api/navigation/getNodes_left-menu', JSON.stringify(data));
        }
      );
    }
  }

  // --- Title case normalization ---

  private toTitleCase(text: string): string {
    if (!text) return '';
    // If the text is all uppercase (like "ZBIRKE"), convert to title case
    if (text === text.toUpperCase() && text.length > 1) {
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }
    return text;
  }

  // --- Build recursive display tree ---

  private toDisplayNode(node: any, depth: number): DisplayNode | null {
    let title = this.translationsService.translate(node.OriginalTitle || node.Title) || node.Title;
    if (!title) return null;
    title = this.toTitleCase(title);

    let visibleChildren = this.getVisibleChildren(node.Children);
    let url = node.Url || '';

    // Absorb same-named child: if a child has the same title as this node,
    // pull its grandchildren up and drop the duplicate entry
    const sameIdx = visibleChildren.findIndex(c => {
      const ct = this.toTitleCase(this.translationsService.translate(c.OriginalTitle || c.Title) || c.Title);
      return ct === title;
    });
    if (sameIdx >= 0) {
      const sameChild = visibleChildren[sameIdx];
      const others = visibleChildren.filter((_, i) => i !== sameIdx);
      const grandchildren = this.getVisibleChildren(sameChild.Children);
      // The absorbed child may be a plain link (e.g. Arhiv > Arhiv list node): the parent has
      // no URL of its own, so it must take over the child's URL - otherwise absorbing turns
      // the section into a dead, unclickable leaf.
      if (!url && sameChild.Url) {
        url = sameChild.Url;
      }
      visibleChildren = [...grandchildren, ...others];
    }

    if (visibleChildren.length === 0) {
      return { id: node.Id, title, icon: node.Icon, url, children: [], isLeaf: true, depth };
    }

    const childNodes = visibleChildren
      .map(child => this.toDisplayNode(child, depth + 1))
      .filter(n => n !== null) as DisplayNode[];

    return { id: node.Id, title, icon: node.Icon, url, children: childNodes, isLeaf: false, depth };
  }

  /** Filter children: evaluate NavigationNodeRule.Condition, keep Type==1 or Type==3 */
  private getVisibleChildren(children: any[]): any[] {
    if (!children) return [];
    return children.filter(child => {
      if (!child) return false;
      let condition = true;
      if (child.NavigationNodeRule?.Condition?.length > 0) {
        try { condition = eval(child.NavigationNodeRule.Condition); } catch { condition = false; }
      }
      return condition && child.Type === 1;
    });
  }

  // --- Navigation ---

  navigateToUrl(url: string) {
    if (url?.trim()) {
      this.utility.processNavigationUrl(url);
    }
  }

  navigateToDashboard() {
    this.router.navigateByUrl('/cms/dashboard');
  }

  // --- Expand/collapse (accordion at top level) ---

  toggleSection(id: number) {
    const s = new Set<number>();
    const current = this.expandedNodes();
    const topLevelIds = new Set(this.sectionIds());

    // Keep sub-level expansions
    for (const eid of current) {
      if (!topLevelIds.has(eid)) s.add(eid);
    }

    // Toggle this section
    if (!current.has(id)) {
      s.add(id);
    }

    this.expandedNodes.set(s);
  }

  toggleNode(id: number) {
    const s = new Set(this.expandedNodes());
    if (s.has(id)) s.delete(id); else s.add(id);
    this.expandedNodes.set(s);
  }

  isExpanded(id: number): boolean {
    return this.expandedNodes().has(id);
  }

  /** Auto-expand the section that contains the active route */
  private autoExpandActiveSection() {
    const nodes = this.displayNodes();
    for (const node of nodes) {
      if (!node.isLeaf && this.isSectionActive(node)) {
        const s = new Set(this.expandedNodes());
        s.add(node.id);
        this.expandedNodes.set(s);
        break;
      }
    }
  }

  // --- Active state detection ---

  isNodeUrlActive(navUrl: string): boolean {
    if (!navUrl) return false;
    const current = decodeURIComponent(this.currentUrl());
    const decodedNav = decodeURIComponent(navUrl);

    // Direct match (existing logic)
    if (this.urlsMatch(decodedNav, current)) return true;

    // Breadcrumb match: check if navUrl matches any URL in the current route's breadcrumb chain
    const bcUrls = getBreadcrumbUrls();
    for (const bcUrl of bcUrls) {
      const normBc = decodeURIComponent(bcUrl).replace(/^\/+|\/+$/g, '');
      const normNav = decodedNav.replace(/^\/+|\/+$/g, '');
      if (normBc === normNav) return true;
    }

    return false;
  }

  private urlsMatch(navUrl: string, currentUrl: string): boolean {
    const [navPath, navQuery] = navUrl.split('?');
    const [currentPath, currentQuery] = currentUrl.split('?');

    // Strip detail-page params (id, view) from current URL before comparing
    const strippedCurrentQuery = this.stripDetailParams(currentQuery);

    const normNav = navPath.replace(/^\/+|\/+$/g, '');
    const normCurrent = currentPath.replace(/^\/+|\/+$/g, '');

    if (normNav !== normCurrent) {
      const urlParam = this.extractParam(navUrl, 'url');
      if (urlParam) {
        const currentSegment = normCurrent.split('/').pop() || '';
        if (urlParam === currentSegment || urlParam === 'cms/' + currentSegment) {
          return this.allNavParamsMatch(navQuery, strippedCurrentQuery);
        }
      }
      return false;
    }

    if (navQuery) {
      return this.allNavParamsMatch(navQuery, strippedCurrentQuery);
    }

    return true;
  }

  /** Remove detail-page-specific params (id, view) so listing menu items match detail pages */
  private stripDetailParams(query: string | undefined): string | undefined {
    if (!query) return query;
    return query
      .split('&')
      .filter(p => {
        const key = p.split('=')[0];
        return key !== 'id' && key !== 'view';
      })
      .join('&') || undefined;
  }

  /** Check that every query parameter in navQuery exists with the same value in currentQuery */
  private allNavParamsMatch(navQuery: string | undefined, currentQuery: string | undefined): boolean {
    if (!navQuery) return true;
    if (!currentQuery) return false;

    const navParams = this.parseParams(navQuery);
    const currentParams = this.parseParams(currentQuery);

    for (const [key, value] of navParams) {
      const currentValue = currentParams.get(key);
      if (currentValue !== value) return false;
    }
    return true;
  }

  private parseParams(query: string): Map<string, string> {
    const map = new Map<string, string>();
    for (const part of query.split('&')) {
      const eqIdx = part.indexOf('=');
      if (eqIdx >= 0) {
        map.set(part.substring(0, eqIdx), part.substring(eqIdx + 1));
      }
    }
    return map;
  }

  private extractParam(url: string, key: string): string | null {
    const idx = url.indexOf(key + '=');
    if (idx < 0) return null;
    const start = idx + key.length + 1;
    const end = url.indexOf('&', start);
    return end < 0 ? url.substring(start) : url.substring(start, end);
  }

  isSectionActive(node: DisplayNode): boolean {
    if (this.isNodeUrlActive(node.url)) return true;
    return this.isAnyChildActive(node);
  }

  private isAnyChildActive(node: DisplayNode): boolean {
    for (const child of node.children) {
      if (this.isNodeUrlActive(child.url)) return true;
      if (this.isAnyChildActive(child)) return true;
    }
    return false;
  }

  // --- CSS class builders ---

  itemClasses(active: boolean): string {
    const base = 'flex items-center gap-2 px-2.5 py-[6px] rounded-lg font-medium transition-all cursor-pointer';
    return active
      ? `${base} bg-primary-100 dark:bg-primary-900/30 text-primary-500 dark:text-primary-300`
      : `${base} text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-800 dark:hover:text-slate-200`;
  }

  sectionClasses(active: boolean): string {
    const base = 'flex items-center gap-2 px-2.5 py-[6px] rounded-lg font-medium transition-all cursor-pointer w-full';
    return active
      ? `${base} bg-primary-100 dark:bg-primary-900/30 text-primary-500 dark:text-primary-300`
      : `${base} text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-800 dark:hover:text-slate-200`;
  }

  childClasses(active: boolean, depth: number): string {
    const base = 'pl-3.5 pr-2 py-[5px] text-[12.5px] transition-all cursor-pointer block';
    return active
      ? `${base} text-primary-500 dark:text-primary-400 font-semibold bg-primary-50/80 dark:bg-primary-900/10 border-l-2 border-primary-500 -ml-[1px]`
      : `${base} text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200`;
  }

  // --- Icon mapping ---

  mapIcon(icon: string | null): string {
    if (!icon) return 'article';
    const map: Record<string, string> = {
      'dashboardv2': 'dashboard', 'dashboard': 'dashboard',
      'collections': 'museum', 'documentation': 'description',
      'registries': 'list_alt', 'mediatec': 'photo_library',
      'inventory': 'fact_check', 'pedagogy': 'school',
      'settings': 'settings', 'statistic': 'bar_chart',
      'restorer': 'build', 'library': 'menu_book',
      'archive': 'archive', 'galislite': 'widgets',
      'home': 'home', 'list': 'list', 'file': 'description',
      'cog': 'settings', 'users': 'group', 'user': 'person',
      'chart': 'bar_chart', 'calendar': 'calendar_today',
      'envelope': 'mail', 'folder': 'folder', 'search': 'search',
      'edit': 'edit', 'photo': 'photo_library', 'image': 'image',
      'book': 'menu_book', 'database': 'database', 'globe': 'language',
      'star': 'star', 'muzej': 'museum',
      'project': 'account_tree', 'projekt': 'account_tree',
      'investment': 'account_tree', 'location': 'location_on',
      'lokacij': 'location_on', 'map': 'map',
    };
    const lower = icon.toLowerCase();
    for (const [key, value] of Object.entries(map)) {
      if (lower.includes(key)) return value;
    }
    if (/^[a-z][a-z0-9_]*$/.test(lower)) {
      return lower;
    }
    return 'article';
  }
}
